import { Fragment, type ReactNode } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/utils/cn'
import { Checkbox } from '@/components/ui/checkbox'

interface FolderCardProps {
  icon: ReactNode
  title: string
  subtitle: string
  onClick: () => void
  className?: string
  selectable?: boolean
  selected?: boolean
  onSelect?: () => void
}

export function FolderCard({
  icon,
  title,
  subtitle,
  onClick,
  className,
  selectable,
  selected,
  onSelect,
}: FolderCardProps) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={cn(
        'group flex min-h-24 min-w-0 items-center gap-3 rounded-xl border border-border bg-card p-4 text-left',
        'hover:border-primary/40 hover:bg-accent/20 hover:shadow-sm',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'transition-[border-color,background-color,box-shadow] cursor-pointer',
        selected && 'border-primary bg-accent/30',
        className
      )}
    >
      {selectable && (
        <div
          className="shrink-0"
          onClick={e => {
            e.stopPropagation()
          }}
        >
          <Checkbox
            aria-label={`Select ${title}`}
            checked={selected}
            onCheckedChange={() => onSelect?.()}
          />
        </div>
      )}
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/60 text-primary [&>svg]:h-5 [&>svg]:w-5">
        {icon}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="line-clamp-2 text-sm font-medium leading-5 text-foreground [overflow-wrap:anywhere]">
          {title.split('/').map((part, index, parts) => (
            <Fragment key={index}>
              {part}
              {index < parts.length - 1 && (
                <>
                  /<wbr />
                </>
              )}
            </Fragment>
          ))}
        </span>
        <span className="text-xs text-muted-foreground">{subtitle}</span>
      </div>
      <ChevronRight
        aria-hidden="true"
        className="h-4 w-4 shrink-0 text-muted-foreground/60 group-hover:text-primary"
      />
    </button>
  )
}
